import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image, X, Loader2, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { API_BASE } from '../lib/api';
import { smartCompressImage, formatSize } from '../lib/imageCompression';

interface MediaItem {
  key: string;
  name: string;
  url: string;
}

interface UploadState {
  name: string;
  status: 'uploading' | 'done' | 'failed';
  info?: string;
  error?: string;
}

interface MediaPickerProps {
  selectedUrl?: string | null;
  onSelect: (url: string) => void;
  onClose: () => void;
  /** Upload category recorded in media_files (e.g. 'product'). Defaults to 'general'. */
  category?: string;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const ACCEPT_ATTR = ALLOWED_TYPES.join(',');

const MediaPicker: React.FC<MediaPickerProps> = ({ selectedUrl, onSelect, onClose, category = 'general' }) => {
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [dragOver, setDragOver] = useState(false);
  // 上傳格式：WebP 預設（檔案較細）；WhatsApp 圖片訊息（優惠券/廣播）必須用 JPG
  const [uploadFormat, setUploadFormat] = useState<'webp' | 'jpeg'>('webp');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem('admin_token');

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media`, {
        headers: { Authorization: `Bearer ${token || ''}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMediaList(Array.isArray(data) ? data : []);
      } else {
        setError('載入媒體庫失敗');
      }
    } catch (e) {
      setError('載入媒體庫失敗');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;
    const invalid = files.filter((f) => !ALLOWED_TYPES.includes(f.type));
    if (invalid.length > 0) {
      setError(`不支援的檔案格式：${invalid.map((f) => f.name).join(', ')}（只支援 JPG / PNG / GIF / WEBP / SVG）`);
      return;
    }
    setError('');
    setUploads(files.map((f) => ({ name: f.name, status: 'uploading' as const })));

    const uploadedUrls: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const result = await smartCompressImage(file, { format: uploadFormat }).catch(() => null);
        const uploadFile = result?.file ?? file;
        const info = result?.isCompressed
          ? `${formatSize(result.originalSize)} → ${formatSize(result.compressedSize)}（${result.ratio}）`
          : undefined;
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('category', category);
        const res = await fetch(`${API_BASE}/api/public/admin/media/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || data.detail || `HTTP ${res.status}`);
        uploadedUrls.push(String(data.url || ''));
        setUploads((prev) => prev.map((u, idx) => (idx === i ? { ...u, status: 'done', info } : u)));
      } catch (e) {
        setUploads((prev) =>
          prev.map((u, idx) => (idx === i ? { ...u, status: 'failed', error: e instanceof Error ? e.message : '上傳失敗' } : u))
        );
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    await fetchMedia();
    // Auto-select the first newly uploaded file so the form field is filled immediately
    const firstUrl = uploadedUrls.filter(Boolean)[0];
    if (firstUrl) onSelect(firstUrl);
  };

  const extractName = (key: string): string => {
    const fileName = key.replace(/^media\//, '');
    const match = fileName.match(/^(\d+)_(.+?)(\.[^.]+)?$/);
    if (match) return match[2].replace(/_/g, ' ');
    return fileName.replace(/\.[^.]+$/, '').replace(/_/g, ' ');
  };

  const busyUploading = uploads.some((u) => u.status === 'uploading');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Image size={20} className="text-red-600" />
            媒體庫
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {/* 格式選擇：WebP 預設；WhatsApp 圖片訊息（優惠券/廣播）必須 JPG */}
          <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
            <span className="text-xs text-gray-400 dark:text-gray-500">上傳格式</span>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setUploadFormat('webp')}
                className={`px-3 py-1.5 transition ${uploadFormat === 'webp' ? 'bg-red-600 text-white font-bold' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                WebP（預設）
              </button>
              <button
                type="button"
                onClick={() => setUploadFormat('jpeg')}
                className={`px-3 py-1.5 transition border-l border-gray-300 dark:border-gray-600 ${uploadFormat === 'jpeg' ? 'bg-red-600 text-white font-bold' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                title="WhatsApp 圖片訊息（優惠券／廣播）必須用 JPG——WebP 會被當成 sticker，對方收唔到圖"
              >
                JPG（WhatsApp 用）
              </button>
            </div>
          </div>

          {/* Upload zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              uploadFiles(Array.from(e.dataTransfer.files || []));
            }}
            onClick={() => !busyUploading && fileInputRef.current?.click()}
            className={`mb-4 rounded-xl border-2 border-dashed p-4 text-center cursor-pointer transition select-none ${
              dragOver
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-red-400 dark:hover:border-red-400'
            } ${busyUploading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              className="hidden"
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
            />
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              {busyUploading ? <Loader2 size={16} className="animate-spin text-red-600" /> : <Upload size={16} className="text-red-600" />}
              {busyUploading ? '上傳中…' : '上傳新圖片：拖放到此處或點擊選擇（可多選，自動壓縮）'}
            </div>
            {uploads.length > 0 && (
              <div className="mt-2 space-y-1 text-left">
                {uploads.map((u, idx) => (
                  <div key={`${u.name}-${idx}`} className="flex items-center gap-2 text-xs">
                    {u.status === 'uploading' && <Loader2 size={13} className="animate-spin text-gray-400 shrink-0" />}
                    {u.status === 'done' && <CheckCircle size={13} className="text-green-600 shrink-0" />}
                    {u.status === 'failed' && <AlertCircle size={13} className="text-red-500 shrink-0" />}
                    <span className="truncate text-gray-700 dark:text-gray-300">{u.name}</span>
                    {u.info && <span className="text-gray-400 shrink-0">{u.info}</span>}
                    {u.error && <span className="text-red-500 shrink-0">{u.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="mb-3 p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Loader2 size={24} className="animate-spin mr-2" />
              載入中...
            </div>
          ) : mediaList.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              媒體庫為空——直接在上面上傳第一張圖片！
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {mediaList.map((item) => {
                const selected = selectedUrl === item.url;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => onSelect(item.url)}
                    className={`group relative aspect-square rounded-lg overflow-hidden border-2 text-left transition ${
                      selected ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-200 dark:border-gray-700 hover:border-red-400'
                    }`}
                    title={extractName(item.key)}
                  >
                    <img
                      src={item.url}
                      alt={extractName(item.key)}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                      {extractName(item.key)}
                    </div>
                    {selected && (
                      <div className="absolute inset-0 flex items-center justify-center bg-red-600/20">
                        <span className="bg-red-600 text-white text-xs px-2 py-1 rounded">已選</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
